import * as React from "react";
import { Fragment, useEffect, useState } from "react";
import {
    Table,
    Button,
    Input,
    Select,
    Modal,
    Spin,
    Typography,
    Row,
    Col,
    Tooltip,
    DatePicker,
    Space
} from "antd";
import { toast } from "react-toastify";
import { BellFilled, EditOutlined, SearchOutlined, ClearOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import { fetchPlaces } from "../../api/places";
import Place from "../../types/Place";
import { addMediatorScore, fetchMediators } from "../../api/mediator";
import { operationNames, userHasAccess } from "../../data/pages";
import AddMediator from "./AddMediator";
import { isTodayBirthDay, parseDate } from "../../util/date";
import MediatorsResponse from "../../types/MediatorsResponse";
import EditMediatorModal from "./EditMediatorModal";
import Mediator from "../../types/Mediator";
import "./MediatorsView.css";
import { decimalRegex2 } from "../../data/constants";
import { initialMediatorScoreState, mediatorsViewFilterInitialState, updatingMediatorInitialState } from "./states"
import { MediatorViewFiltersType } from "./types"
import { useTexts } from "../../hooks/useTexts";
import { getUserFromToken } from "../../data/storage";
import UserRole from "../../types/UserRole";
import TablePagination from "../TablePagination/TablePagination";

const { Title } = Typography;

const MediatorsView = () => {
    const { t } = useTexts();
    const [data, setData] = useState<MediatorsResponse>({
        mediators: [],
        pagination: { page: 0, pageSize: 10, pageCount: 0, elementCount: 0 },
    });
    const [places, setPlaces] = useState<Place[]>([]);
    const [loading, setLoading] = useState(false);
    const [updatingMediatorState, setUpdatingMediatorState] = useState<Mediator>(updatingMediatorInitialState);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [scoreState, setScoreState] = useState(initialMediatorScoreState);
    const [selectedFilters, setSelectedFilters] = useState<MediatorViewFiltersType>(mediatorsViewFilterInitialState);

    const userData = getUserFromToken();
    const isManager = userData?.roles?.some(role => role.id === UserRole.MANAGER);

    const openModal = () => setOpen(true);
    const closeModal = () => setOpen(false);

    useEffect(() => {
        getMediators(selectedFilters);
        getPlaces();
    }, []);

    const getMediators = (filters: MediatorViewFiltersType) => {
        setLoading(true);
        fetchMediators(getParams(filters))
            .then((response) => {
                if (response) {
                    setData(response);
                    setSelectedFilters((prev) => ({
                        ...prev,
                        page: response.pagination.page,
                        pageSize: response.pagination.pageSize,
                    }));
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    const getPlaces = () => {
        fetchPlaces()
            .then((response) => setPlaces(response?.content || []))
            .catch(console.error);
    };

    const getParams = (filters: MediatorViewFiltersType) => ({
        placeId: filters.placeId || [],
        page: filters.page ?? null,
        pageSize: filters.pageSize ?? null,
        firstname: filters.firstname ?? null,
        lastname: filters.lastname ?? null,
        birthDate: parseDate(filters.birthDate),
    });

    const showOnlyBDays = (filters: MediatorViewFiltersType) => {
        setLoading(true);
        fetchMediators({
            page: filters.page ?? null,
            pageSize: filters.pageSize ?? null,
            showBDays: true,
        })
            .then((response) => {
                if (response) {
                    setData(response);
                    setSelectedFilters((prev) => ({
                        ...prev,
                        page: response.pagination.page,
                        pageSize: response.pagination.pageSize,
                    }));
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    const handleSubmit = () => {
        setLoading(true);
        const scoreStr = String(scoreState.score).replace(",", ".");
        const body = { score: Number(scoreStr), type: scoreState.type };

        addMediatorScore(scoreState.mediatorId, body)
            .then(() => {
                toast.success(t('mediator', 'scoreAdded'), { theme: "colored" });
                getMediators(selectedFilters);
            })
            .catch(console.error)
            .finally(() => {
                setScoreState(initialMediatorScoreState);
                setOpen(false);
                setLoading(false);
            });
    };

    const applyFilters = () => {
        getMediators(selectedFilters);
    };

    const clearFilter = () => {
        setSelectedFilters(mediatorsViewFilterInitialState);
        getMediators(mediatorsViewFilterInitialState);
    };

    const columns: ColumnsType<Mediator> = [
        {
            title: '#',
            key: 'index',
            render: (text, record, index) => ((data.pagination.page - 1) * data.pagination.pageSize) + index + 1,
            width: 60,
        },
        {
            title: t('mediator', 'firstname'),
            dataIndex: 'firstname',
            key: 'firstname',
        },
        {
            title: t('mediator', 'lastname'),
            dataIndex: 'lastname',
            key: 'lastname',
        },
        {
            title: t('mediator', 'phone'),
            dataIndex: 'phoneNumber',
            key: 'phoneNumber',
        },
        {
            title: t('mediator', 'birthDate'),
            key: 'birthDate',
            render: (_, record) => (
                <Space>
                    {record.birthDate}
                    {isTodayBirthDay(record.birthDate) && (
                        <Tooltip title={t('mediator', 'birthdayToday')}>
                            <BellFilled style={{ color: "gold" }} />
                        </Tooltip>
                    )}
                </Space>
            )
        },
        {
            title: t('mediator', 'placeObject'),
            key: 'placeObject',
            render: (_, record) => `${record.place.name} (${record.place.city})`
        },
        {
            title: '',
            key: 'addScore',
            render: (_, record) => (
                <Button
                    type="primary"
                    disabled={isManager}
                    onClick={() => {
                        setScoreState({ ...scoreState, mediatorId: record.id });
                        openModal();
                    }}
                >
                    {t('mediator', 'addScore')}
                </Button>
            )
        }
    ];

    if (userHasAccess(operationNames.EDIT_MEDIATOR)) {
        columns.push({
            title: '',
            key: 'action',
            width: 60,
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => {
                        setUpdatingMediatorState(record);
                        setEditModalOpen(true);
                    }}
                />
            )
        });
    }

    return (
        <Fragment>
            <Modal
                title={t('mediator', 'addScore')}
                open={open}
                onCancel={closeModal}
                footer={null}
                width={500}
            >
                <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", gap: "16px" }}>
                    <Input
                        placeholder={t('mediator', 'score')}
                        value={scoreState.score || ""}
                        disabled={isManager}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                            let value = event.target.value;
                            if (decimalRegex2.test(value) || value === "") {
                                if (value?.startsWith(",")) {
                                    value = "0" + value;
                                }
                                setScoreState({ ...scoreState, score: value });
                            }
                        }}
                        style={{ flex: 1 }}
                    />
                    <Select
                        placeholder={t('mediator', 'scoreType')}
                        value={!!scoreState.type ? (scoreState.type as string) : undefined}
                        disabled={isManager}
                        onChange={(value) => setScoreState({ ...scoreState, type: value })}
                        style={{ flex: 1 }}
                        options={[
                            { label: t('mediator', 'initialScore'), value: "INITIAL" },
                            { label: t('mediator', 'giftScore'), value: "GIFT" }
                        ]}
                        allowClear
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button onClick={closeModal}>
                        {t('common', 'close')}
                    </Button>
                    <Button
                        type="primary"
                        disabled={loading || !scoreState.score || isManager}
                        onClick={handleSubmit}
                        loading={loading}
                    >
                        {t('mediator', 'addScore')}
                    </Button>
                </div>
            </Modal>

            <EditMediatorModal
                open={editModalOpen}
                state={updatingMediatorState}
                setState={setUpdatingMediatorState}
                successCallback={() => {
                    setEditModalOpen(false);
                    getMediators(selectedFilters);
                }}
                closeModal={() => {
                    setEditModalOpen(false);
                    setUpdatingMediatorState(updatingMediatorInitialState);
                }}
            />

            <div style={{ marginTop: '50px' }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder={t('user', 'firstname')}
                            value={selectedFilters.firstname || ""}
                            onChange={(e) => setSelectedFilters({ ...selectedFilters, firstname: e.target.value })}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder={t('user', 'lastname')}
                            value={selectedFilters.lastname || ""}
                            onChange={(e) => setSelectedFilters({ ...selectedFilters, lastname: e.target.value })}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder={t('place', 'selectPlace')}
                            value={selectedFilters.placeId}
                            onChange={(value) => setSelectedFilters({ ...selectedFilters, placeId: value })}
                            options={places.map(p => ({ label: p.name, value: p.id }))}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder={t('mediator', 'birthDate')}
                            value={selectedFilters.birthDate ? dayjs(selectedFilters.birthDate) : null}
                            onChange={(date) => setSelectedFilters({ ...selectedFilters, birthDate: date })}
                            format="DD/MM/YYYY"
                        />
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: "16px" }} justify="space-between" align="middle">
                    <Col xs={24} sm={6}>
                        {userHasAccess(operationNames.CREATE_MEDIATOR) && (
                            <AddMediator successCallback={() => getMediators(selectedFilters)} />
                        )}
                    </Col>
                    <Col xs={24} sm={18} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Button type="default" onClick={() => showOnlyBDays(selectedFilters)} loading={loading}>
                            {t('mediator', 'showBirthdays')}
                        </Button>
                        <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters} loading={loading}>
                            {t('prescription', 'applyFilter')}
                        </Button>
                        <Button danger icon={<ClearOutlined />} onClick={clearFilter} />
                    </Col>
                </Row>

                <Title level={4} style={{ marginTop: '24px' }}>
                    {t('mediator', 'totalMediatorCount')}: {data.pagination?.elementCount || 0}
                </Title>

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
                        <Spin size="large" />
                    </div>
                ) : data.mediators?.length > 0 ? (
                    <div style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', marginTop: '24px' }}>
                        <Table
                            columns={columns}
                            dataSource={data.mediators}
                            rowKey="id"
                            scroll={{ x: 'max-content' }}
                            pagination={false}
                        />
                        <TablePagination
                            current={data.pagination.page}
                            pageSize={data.pagination.pageSize}
                            pageCount={data.pagination.pageCount}
                            total={data.pagination.elementCount}
                            onChange={(page, pageSize) => getMediators({ ...selectedFilters, page, pageSize })}
                        />
                    </div>
                ) : (
                    <Title level={4} style={{ textAlign: 'center', marginTop: '50px' }}>
                        {t('mediator', 'mediatorNotFound')}
                    </Title>
                )}
            </div>
        </Fragment>
    );
};

export default MediatorsView;